import { ref, computed, watch, type Ref } from 'vue';

// Validation rule types
export interface ValidationRule {
  validate: (value: any, field?: string, formData?: Record<string, any>) => boolean | string;
  message?: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

export interface FieldValidation {
  value: Ref<any>;
  rules: ValidationRule[];
  errors: Ref<string[]>;
  isValid: Ref<boolean>;
  isDirty: Ref<boolean>;
  isTouched: Ref<boolean>;
  validate: (formData?: Record<string, any>, fieldName?: string) => boolean;
  reset: () => void;
  touch: () => void;
}

export interface FormValidation {
  fields: Record<string, FieldValidation>;
  isValid: Ref<boolean>;
  errors: Ref<Record<string, string[]>>;
  isDirty: Ref<boolean>;
  validate: () => boolean;
  validateField: (fieldName: string) => boolean;
  reset: () => void;
  markAllTouched: () => void;
  getFormData: () => Record<string, any>;
}

// Built-in validation rules
export const validationRules = {
  required: (message = 'This field is required'): ValidationRule => ({
    validate: (value) => {
      if (typeof value === 'string') return value.trim().length > 0;
      if (Array.isArray(value)) return value.length > 0;
      return value !== null && value !== undefined && value !== '';
    },
    message
  }),

  minLength: (min: number, message?: string): ValidationRule => ({
    validate: (value) => !value || value.toString().length >= min,
    message: message || `Must be at least ${min} characters long`
  }),

  maxLength: (max: number, message?: string): ValidationRule => ({
    validate: (value) => !value || value.toString().length <= max,
    message: message || `Must be no more than ${max} characters long`
  }),

  min: (min: number, message?: string): ValidationRule => ({
    validate: (value) => value === '' || value === null || value === undefined || Number(value) >= min,
    message: message || `Must be at least ${min}`
  }),

  max: (max: number, message?: string): ValidationRule => ({
    validate: (value) => value === '' || value === null || value === undefined || Number(value) <= max,
    message: message || `Must be no more than ${max}`
  }),

  email: (message = 'Please enter a valid email address'): ValidationRule => ({
    validate: (value) => {
      if (!value) return true; // Allow empty unless required
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(value);
    },
    message
  }),

  url: (message = 'Please enter a valid URL'): ValidationRule => ({
    validate: (value) => {
      if (!value) return true; // Allow empty unless required
      try {
        new URL(value);
        return true;
      } catch {
        return false;
      }
    },
    message
  }),

  pattern: (regex: RegExp, message = 'Invalid format'): ValidationRule => ({
    validate: (value) => !value || regex.test(value),
    message
  }),

  numeric: (message = 'Must be a valid number'): ValidationRule => ({
    validate: (value) => !value || !isNaN(Number(value)),
    message
  }),

  integer: (message = 'Must be a whole number'): ValidationRule => ({
    validate: (value) => !value || (Number.isInteger(Number(value)) && !value.toString().includes('.')),
    message
  }),

  oneOf: (options: any[], message?: string): ValidationRule => ({
    validate: (value) => !value || options.includes(value),
    message: message || `Must be one of: ${options.join(', ')}`
  }),

  custom: (fn: (value: any, field?: string, formData?: Record<string, any>) => boolean | string, message = 'Invalid value'): ValidationRule => ({
    validate: fn,
    message
  })
};

// Validation composable for individual fields
export function useFieldValidation(initialValue: any = '', rules: ValidationRule[] = []) {
  const value = ref(initialValue);
  const errors = ref<string[]>([]);
  const isDirty = ref(false);
  const isTouched = ref(false);

  const isValid = computed(() => errors.value.length === 0);

  const validate = (formData?: Record<string, any>, fieldName?: string): boolean => {
    errors.value = [];

    for (const rule of rules) {
      const result = rule.validate(value.value, fieldName, formData);
      
      if (result === false) {
        errors.value.push(rule.message || 'Invalid value');
      } else if (typeof result === 'string') {
        errors.value.push(result);
      }
    }

    return errors.value.length === 0;
  };

  const reset = () => {
    value.value = initialValue;
    errors.value = [];
    isDirty.value = false;
    isTouched.value = false;
  };

  const touch = () => {
    isTouched.value = true;
  };

  // Watch for changes to mark as dirty
  watch(value, (newValue) => {
    if (newValue !== initialValue) {
      isDirty.value = true;
    }
  });

  return {
    value,
    rules,
    errors,
    isValid,
    isDirty,
    isTouched,
    validate,
    reset,
    touch
  };
}

// Validation composable for forms
export function useFormValidation(fieldDefinitions: Record<string, { initialValue?: any; rules?: ValidationRule[] }> = {}): FormValidation {
  const fields: Record<string, FieldValidation> = {};

  // Initialize fields
  for (const [fieldName, definition] of Object.entries(fieldDefinitions)) {
    const field = useFieldValidation(definition.initialValue, definition.rules || []);
    fields[fieldName] = field;
  }

  const isValid = computed(() => 
    Object.values(fields).every(field => field.isValid.value)
  );

  const errors = computed(() => {
    const result: Record<string, string[]> = {};
    for (const [fieldName, field] of Object.entries(fields)) {
      if (field.errors.value.length > 0) {
        result[fieldName] = field.errors.value;
      }
    }
    return result;
  });

  const isDirty = computed(() => 
    Object.values(fields).some(field => field.isDirty.value)
  );

  const validate = (): boolean => {
    const formData = getFormData();
    let isFormValid = true;

    for (const [fieldName, field] of Object.entries(fields)) {
      const fieldIsValid = field.validate(formData, fieldName);
      if (!fieldIsValid) {
        isFormValid = false;
      }
    }

    return isFormValid;
  };

  const validateField = (fieldName: string): boolean => {
    const field = fields[fieldName];
    if (!field) return true;

    const formData = getFormData();
    return field.validate(formData, fieldName);
  };

  const reset = () => {
    for (const field of Object.values(fields)) {
      field.reset();
    }
  };

  const markAllTouched = () => {
    for (const field of Object.values(fields)) {
      field.touch();
    }
  };

  const getFormData = (): Record<string, any> => {
    const result: Record<string, any> = {};
    for (const [fieldName, field] of Object.entries(fields)) {
      result[fieldName] = field.value.value;
    }
    return result;
  };

  // Add a field dynamically
  const addField = (fieldName: string, initialValue: any = '', rules: ValidationRule[] = []) => {
    fields[fieldName] = useFieldValidation(initialValue, rules);
  };

  // Remove a field
  const removeField = (fieldName: string) => {
    delete fields[fieldName];
  };

  return {
    fields,
    isValid,
    errors,
    isDirty,
    validate,
    validateField,
    reset,
    markAllTouched,
    addField,
    removeField,
    getFormData
  } as FormValidation & { 
    addField: (fieldName: string, initialValue?: any, rules?: ValidationRule[]) => void;
    removeField: (fieldName: string) => void;
    getFormData: () => Record<string, any>;
  };
}

// Utility function to create common validation scenarios
export const createValidationSchema = {
  email: (required = false) => [
    ...(required ? [validationRules.required()] : []),
    validationRules.email()
  ],

  password: (minLength = 8, required = true) => [
    ...(required ? [validationRules.required()] : []),
    validationRules.minLength(minLength),
    validationRules.pattern(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
      'Password must contain at least one uppercase letter, one lowercase letter, and one number'
    )
  ],

  url: (required = false) => [
    ...(required ? [validationRules.required()] : []),
    validationRules.url()
  ],

  positiveNumber: (required = false) => [
    ...(required ? [validationRules.required()] : []),
    validationRules.numeric(),
    validationRules.min(0, 'Must be a positive number')
  ],

  port: (required = false) => [
    ...(required ? [validationRules.required()] : []),
    validationRules.integer(),
    validationRules.min(1, 'Port must be between 1 and 65535'),
    validationRules.max(65535, 'Port must be between 1 and 65535')
  ]
};
